import React from 'react';
import { AccountPageLayout } from 'pages/Account/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, TextField } from '@clariness/clarikit-react';
import { useMultiStep } from 'components/MultiStep';
import { Schemas, useFormValidation } from 'utils/validation';
import { validateNewPasswordStrength } from 'utils/validation/validateNewPasswordStrength';
import { Services } from 'services';
import { ChangeCurrentPasswordDto } from 'common/api';
import { Events } from '@clariness/tracking-event-runtime';
import { captureEventV2 } from 'utils/events/captureEvent';

export const AccountDetailsChangePassword = () => {
  const { nextStep, prevStep } = useMultiStep();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useFormValidation();

  const intl = useIntl();

  const { updatePassword, isLoading } =
    Services.SiteEmployees.useUpdatePassword();

  const currentPassword = register('currentPassword', Schemas.password(intl));
  const newPassword = register(
    'newPassword',
    validateNewPasswordStrength(intl)
  );
  const confirmNewPassword = register('confirmNewPassword', {
    validate: (value: string) =>
      value === getValues('newPassword') ||
      intl.formatMessage({
        id: 'is.account_details.change_password.confirm_new_password.password_no_match_validation_message',
        defaultMessage: "Your passwords don't match",
      }),
  });

  return (
    <AccountPageLayout>
      <AccountPageLayout.Heading>
        <FormattedMessage
          id="is.account_details.change_password.heading"
          defaultMessage="Change password"
        />
      </AccountPageLayout.Heading>

      <AccountPageLayout.Form
        onSubmit={handleSubmit((data: ChangeCurrentPasswordDto) => {
          updatePassword(data);
          captureEventV2(
            Events.InvestigatorService.InvestigatorChangedPassword({})
          );
        })}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="10px"
        >
          <Box width="100%">
            <TextField
              {...currentPassword}
              value={watch('currentPassword') || ''}
              onChange={(value) => {
                void currentPassword.onChange({
                  target: { name: currentPassword.name, value },
                });
              }}
              type="password"
              showPasswordButton
              placeholder={intl.formatMessage({
                id: 'is.account_details.change_password.current_password_input_placeholder',
                defaultMessage: 'Current password',
              })}
              error={errors?.currentPassword?.message as string | undefined}
              autoComplete="current-password"
              data-test-id="account-current-password-input"
              label="Current password"
              labelHidden
            />
          </Box>

          <Button
            variant="tertiary"
            onClick={nextStep}
            data-test-id="account-forgot-password-button"
          >
            {intl.formatMessage({
              id: 'is.account_details.change_password.forgot_password_button',
              defaultMessage: 'Forgotten password?',
            })}
          </Button>
        </Box>

        <Box
          display="grid"
          style={{
            columnGap: '54px',
            rowGap: '34px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          }}
        >
          <TextField
            {...newPassword}
            value={watch('newPassword') || ''}
            onChange={(value) => {
              void newPassword.onChange({
                target: { name: newPassword.name, value },
              });
            }}
            type="password"
            showPasswordButton
            placeholder={intl.formatMessage({
              id: 'is.account_details.change_password.new_password_input_placeholder',
              defaultMessage: 'New password',
            })}
            error={errors?.newPassword?.message as string | undefined}
            autoComplete="new-password"
            data-test-id="account-new-password-input"
            label="New password"
            labelHidden
          />

          <TextField
            {...confirmNewPassword}
            value={watch('confirmNewPassword') || ''}
            onChange={(value) => {
              void confirmNewPassword.onChange({
                target: { name: confirmNewPassword.name, value },
              });
            }}
            type="password"
            showPasswordButton
            placeholder={intl.formatMessage({
              id: 'is.account_details.change_password.confirm_new_password_input_placeholder',
              defaultMessage: 'Confirm new password',
            })}
            error={errors?.confirmNewPassword?.message as string | undefined}
            autoComplete="new-password"
            data-test-id="account-confirm-new-password-input"
            label="Confirm new password"
            labelHidden
          />
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Button
            variant="secondary"
            onClick={prevStep}
            data-test-id="account-cancel-password-input"
          >
            {intl.formatMessage({
              id: 'is.account_details.change_password.cancel_button',
              defaultMessage: 'Cancel',
            })}
          </Button>

          <Button
            submit
            loading={isLoading}
            data-test-id="account-save-password-input"
          >
            {intl.formatMessage({
              id: 'is.account_details.change_password.save_button',
              defaultMessage: 'Save',
            })}
          </Button>
        </Box>
      </AccountPageLayout.Form>
    </AccountPageLayout>
  );
};
